/**
 * AfricanPortfolioMap — High-density interactive SVG vector map of 33
 * African sovereign nations across 21 stock exchanges.
 *
 * V34.0 Design Tokens:
 *   Default:   fill-[#090D16] (light) / fill-[#111827] (dark)
 *   Executing: fill-[#2563EB] pulse 600ms → settle fill-[#4B5563]
 *   Hover:     fill-[#1E293B] (light) / fill-[#475569] (dark)
 *   Stroke:    1px #1F2937 (light) / #374151 (dark)
 *   Active:    stroke-[#2563EB] 1.5px ring
 *
 * Desktop: 7+5 col grid — map left, telemetry drawer right.
 * Mobile:  full-width map + bottom-sheet drawer on tap.
 */
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, TrendingUp, BarChart3, X, ChevronUp, Activity, ExternalLink, AlertTriangle } from "lucide-react";
import COUNTRY_PATHS, { EXCHANGE_INFO } from "@/data/africaCountryPaths";
import { CurrencyValue } from "@/components/CurrencyValue";
import { useCurrency } from "@/context/CurrencyContext";
import { signTone, toneVar, signedPrefix } from "@/lib/numberColor";

/* ── Tokens ──────────────────────────────────────────────────────── */
const T = {
    DEFAULT_FILL:     "var(--map-default, #090D16)",
    DARK_DEFAULT:     "#111827",
    HOVER_FILL:       "#1E293B",
    DARK_HOVER:       "#475569",
    EXEC_PULSE:       "#2563EB",
    ACTIVE_FILL:      "#4B5563",
    DARK_ACTIVE_FILL: "#374151",
    ACTIVE_STROKE:    "#2563EB",
    STROKE:           "#1F2937",
    DARK_STROKE:      "#374151",
    BLUE:             "#2563EB",
    ORANGE:           "#EA580C",
    NEUTRAL:          "#6B7280",
};

const VBOX = "0 0 1000 1000";

/* Concentration-risk threshold — matches the heat scale's top tier
 * (exposureHeatColor's >30% bucket). Crossing this in any single country
 * triggers both the persistent banner below and a one-time toast. */
const CONCENTRATION_THRESHOLD_PCT = 30;

/* ── Helpers ─────────────────────────────────────────────────────── */
function getCountryFill(iso, holdings, executingSet) {
    if (executingSet.has(iso)) return T.EXEC_PULSE;
    const h = holdings?.[iso];
    if (h?.status === "holding") return exposureHeatColor(h.allocationPct);
    if (h?.status === "executing") return T.EXEC_PULSE;
    return T.DEFAULT_FILL;
}

/* Exposure heatmap scale — color intensity tracks each country's real
 * share of total open-position exposure (holding.allocationPct, 0-100).
 * Low shares stay a calm blue; concentration above 30% in one country
 * escalates to amber/orange (risk-concentration warning), matching the
 * app's existing blue=alpha / orange=risk color language. */
function exposureHeatColor(sharePct) {
    const p = Math.min(100, Math.max(0, sharePct || 0));
    if (p <= 0) return T.DEFAULT_FILL;
    if (p < 5) return "#1D4ED8";
    if (p < 15) return "#2563EB";
    if (p < 30) return "#D97706";
    return "#EA580C";
}

function getCountryStroke(iso, holdings) {
    const h = holdings?.[iso];
    if (h && h.status !== "idle") return T.ACTIVE_STROKE;
    return T.DARK_STROKE;
}

function getStrokeWidth(iso, holdings) {
    const h = holdings?.[iso];
    if (h && h.status !== "idle") return 1.5;
    return 0.8;
}

/* ── Glassmorphic Tooltip ────────────────────────────────────────── */
function MapTooltip({ country, holding, position, onClose }) {
    if (!country) return null;
    const ex = country.bourse ? EXCHANGE_INFO[country.bourse] : null;
    return (
        <div
            className="absolute z-50 pointer-events-none"
            style={{ left: position.x, top: position.y, transform: "translate(-50%, -110%)" }}
        >
            <div
                className="backdrop-blur-md rounded-lg border border-slate-700 shadow-xl p-3 min-w-[180px]"
                style={{ background: "rgba(9,13,22,0.92)", color: "#fff" }}
            >
                <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700/80 tracking-wider">{country.iso}</span>
                    <span className="text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>{country.name}</span>
                </div>
                {ex && (
                    <div className="text-xs text-slate-400 mb-1" style={{ fontFamily: "'Roboto', sans-serif" }}>
                        {ex.code} &middot; {ex.fullName}
                    </div>
                )}
                {holding && (
                    <div className="space-y-1 pt-1 border-t border-slate-700/60 mt-1">
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">Ticker</span>
                            <span className="font-mono font-semibold">{holding.ticker || "—"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">Exposure</span>
                            <span className="font-mono" style={{ color: T.BLUE }}>{(holding.allocationPct || 0).toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">PnL</span>
                            <span className="font-mono" style={{ color: toneVar(signTone(holding.pnlKes || 0)) }}>
                                {signedPrefix(holding.pnlKes || 0)}{Math.round(holding.pnlKes || 0).toLocaleString()} KES
                            </span>
                        </div>
                    </div>
                )}
                {!holding && !ex && (
                    <div className="text-xs text-slate-500 italic">No exchange listed</div>
                )}
            </div>
        </div>
    );
}

/* ── Bottom Sheet (Mobile) ───────────────────────────────────────── */
function BottomSheet({ country, holding, onClose, onNavigate }) {
    const ex = country?.bourse ? EXCHANGE_INFO[country.bourse] : null;
    if (!country) return null;
    return (
        <div
            className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300"
            data-testid="map-bottom-sheet"
        >
            <div
                className="max-h-[50vh] rounded-t-xl p-4 border-t border-slate-700 shadow-2xl overflow-y-auto"
                style={{ background: "var(--bottom-sheet-bg, #111827)", color: "#fff" }}
            >
                {/* Handle */}
                <div className="w-10 h-1 rounded-full bg-slate-600 mx-auto mb-3" />

                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-700/80 tracking-wider">{country.iso}</span>
                            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>{country.name}</h3>
                        </div>
                        {ex && (
                            <p className="text-sm text-slate-400 mt-0.5" style={{ fontFamily: "'Roboto', sans-serif" }}>
                                {ex.code} &middot; {ex.fullName}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-slate-700/60 transition-colors"
                        data-testid="bottom-sheet-close"
                    >
                        <X className="w-4 h-4 text-slate-400" />
                    </button>
                </div>

                {/* Deep-link to exchange page */}
                {ex && (
                    <button
                        onClick={() => onNavigate && onNavigate(country)}
                        className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 px-3 text-sm font-semibold mb-3 transition-colors"
                        style={{ background: "#2563EB", color: "#fff" }}
                        data-testid="view-exchange-btn-mobile"
                    >
                        <ExternalLink className="w-4 h-4" />
                        View {ex.code} Exchange
                    </button>
                )}

                {/* Metrics */}
                {holding ? (
                    <div className="grid grid-cols-2 gap-3">
                        <MetricTile label="Ticker" value={holding.ticker || "—"} />
                        <MetricTile label="Positions" value={holding.positions ?? "—"} />
                        <MetricTile label="Exposure" value={`${(holding.allocationPct || 0).toFixed(1)}%`} color={T.BLUE} />
                        <MetricTile label="PnL (KES)" value={`${signedPrefix(holding.pnlKes || 0)}${Math.round(holding.pnlKes || 0).toLocaleString()}`} color={toneVar(signTone(holding.pnlKes || 0))} />
                        <MetricTile label="Status" value={holding.status || "idle"} />
                    </div>
                ) : (
                    <div className="text-center py-6 text-slate-500 text-sm">
                        {ex ? "No active holdings on this exchange" : "No exchange listed for this country"}
                    </div>
                )}
            </div>
        </div>
    );
}

function MetricTile({ label, value, color }) {
    return (
        <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5" style={{ fontFamily: "'Roboto', sans-serif" }}>{label}</div>
            <div className="text-sm font-semibold font-mono proportional-nums" style={{ color: color || "#fff" }}>{value}</div>
        </div>
    );
}

/* ── Desktop Telemetry Drawer ────────────────────────────────────── */
function TelemetryDrawer({ country, holding, onClose, onNavigate }) {
    const ex = country?.bourse ? EXCHANGE_INFO[country.bourse] : null;
    if (!country) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <MapPin className="w-8 h-8 text-slate-600 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">Select a country on the map to view exchange telemetry</p>
            </div>
        );
    }
    return (
        <div className="h-full overflow-y-auto p-4 space-y-4" data-testid="map-telemetry-drawer">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700/80 text-white tracking-wider">{country.iso}</span>
                        <h3 className="text-lg font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>{country.name}</h3>
                    </div>
                    {ex && (
                        <p className="text-sm text-muted-foreground mt-0.5" style={{ fontFamily: "'Roboto', sans-serif" }}>
                            {ex.code} &middot; {ex.fullName}
                        </p>
                    )}
                </div>
                <button onClick={onClose} className="p-1 rounded-md hover:bg-secondary transition-colors" data-testid="drawer-close">
                    <X className="w-4 h-4 text-muted-foreground" />
                </button>
            </div>

            {/* Deep-link to exchange page */}
            {ex && (
                <button
                    onClick={() => onNavigate && onNavigate(country)}
                    className="w-full flex items-center justify-center gap-2 rounded-lg py-2 px-3 text-xs font-semibold transition-colors"
                    style={{ background: "#2563EB", color: "#fff" }}
                    data-testid="view-exchange-btn"
                >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View {ex.code} Exchange
                </button>
            )}

            {/* Exchange Info */}
            {ex && (
                <div className="rounded-lg border border-border/60 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Activity className="w-3.5 h-3.5" /> Exchange Details
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Code</div>
                            <div className="font-mono font-semibold">{ex.code}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Currency</div>
                            <div className="font-mono font-semibold">{ex.currency}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Holdings */}
            {holding ? (
                <div className="rounded-lg border border-border/60 p-3 space-y-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <BarChart3 className="w-3.5 h-3.5" /> Live Position Exposure
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Top ticker</span>
                            <span className="font-mono font-semibold">{holding.ticker}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Open positions</span>
                            <span className="font-mono font-semibold">{holding.positions ?? "—"}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Exposure share</span>
                            <span className="font-mono font-semibold" style={{ color: T.BLUE }}>
                                {(holding.allocationPct || 0).toFixed(1)}%
                            </span>
                        </div>
                        {/* Allocation bar */}
                        <div className="w-full h-1.5 rounded-full bg-secondary/60 overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(holding.allocationPct || 0, 100)}%`, background: exposureHeatColor(holding.allocationPct) }}
                            />
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Exposure (KES)</span>
                            <span className="font-mono font-semibold">
                                {Math.round(holding.exposureKes || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">PnL (KES)</span>
                            <span className="font-mono font-semibold" style={{ color: toneVar(signTone(holding.pnlKes || 0)) }}>
                                {signedPrefix(holding.pnlKes || 0)}{Math.round(holding.pnlKes || 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Status</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                holding.status === "executing"
                                    ? "bg-blue-500/20 text-blue-400"
                                    : holding.status === "holding"
                                    ? "bg-emerald-500/20 text-emerald-400"
                                    : "bg-slate-500/20 text-slate-400"
                            }`}>
                                {holding.status}
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    {ex ? (
                        <>
                            <TrendingUp className="w-6 h-6 mx-auto mb-2 opacity-40" />
                            No active holdings on {ex.code}
                        </>
                    ) : (
                        <>
                            <MapPin className="w-6 h-6 mx-auto mb-2 opacity-40" />
                            No exchange in {country.name}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── CSS keyframes (injected once) ───────────────────────────────── */
const STYLE_ID = "african-map-keyframes";
function injectKeyframes() {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
        @keyframes exec-pulse {
            0% { fill: ${T.DEFAULT_FILL}; }
            50% { fill: ${T.EXEC_PULSE}; }
            100% { fill: ${T.ACTIVE_FILL}; }
        }
        .exec-pulse-anim {
            animation: exec-pulse 600ms ease-out forwards;
        }
    `;
    document.head.appendChild(style);
}

/* Stable empty-object reference — used as the `activeHoldings` default so
 * dependency-array identity doesn't change every render. A literal `{}` as
 * a default parameter value is a NEW object on every call, which broke the
 * "Track execution pulses" effect below into an infinite render loop
 * whenever the parent (Dashboard) omits the prop. */
const EMPTY_HOLDINGS = {};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function AfricanPortfolioMap({
    activeHoldings = EMPTY_HOLDINGS,
    onCountrySelect,
    densityMode = "cozy",
}) {
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [hovered, setHovered] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [executingSet, setExecutingSet] = useState(new Set());
    const [isMobile, setIsMobile] = useState(false);
    const svgRef = useRef(null);
    const containerRef = useRef(null);

    const handleNavigateToExchange = useCallback((country) => {
        if (country?.bourse) {
            navigate(`/app/macro/${country.bourse}`);
        }
    }, [navigate]);

    useEffect(() => { injectKeyframes(); }, []);

    // Responsive detection
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 640px)");
        setIsMobile(mq.matches);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // Track execution pulses
    useEffect(() => {
        const executing = new Set();
        for (const [iso, h] of Object.entries(activeHoldings)) {
            if (h.status === "executing") executing.add(iso);
        }
        setExecutingSet(executing);
        if (executing.size > 0) {
            const timer = setTimeout(() => setExecutingSet(new Set()), 700);
            return () => clearTimeout(timer);
        }
    }, [activeHoldings]);

    const countryMap = useMemo(() => {
        const m = {};
        for (const c of COUNTRY_PATHS) m[c.iso] = c;
        return m;
    }, []);

    // Concentration-risk detection — real exposure share per country from
    // the live heatmap data. Persistent banner while any country stays
    // above the threshold; a one-time toast per country per session so a
    // trader isn't re-notified every 30s poll while it stays elevated.
    const concentrated = useMemo(() => {
        return Object.entries(activeHoldings)
            .filter(([, h]) => h?.status === "holding" && (h.allocationPct || 0) > CONCENTRATION_THRESHOLD_PCT)
            .map(([iso, h]) => ({ iso, name: countryMap[iso]?.name || iso, pct: h.allocationPct, ticker: h.ticker }))
            .sort((a, b) => b.pct - a.pct);
    }, [activeHoldings, countryMap]);

    const alertedRef = useRef(new Set());
    useEffect(() => {
        const currentIsos = new Set(concentrated.map((c) => c.iso));
        for (const c of concentrated) {
            if (!alertedRef.current.has(c.iso)) {
                alertedRef.current.add(c.iso);
                toast.warning(
                    `Concentration risk: ${c.pct.toFixed(1)}% of your exposure is in ${c.name}${c.ticker ? ` (${c.ticker})` : ""}.`,
                    { id: `concentration-${c.iso}` },
                );
            }
        }
        // Allow re-alerting if a country drops below the threshold and later climbs back above it.
        for (const iso of Array.from(alertedRef.current)) {
            if (!currentIsos.has(iso)) alertedRef.current.delete(iso);
        }
    }, [concentrated]);

    const handleCountryClick = useCallback((country) => {
        setSelected(country);
        if (onCountrySelect) onCountrySelect(country.iso);
    }, [onCountrySelect]);

    const handleMouseMove = useCallback((e, country) => {
        if (isMobile) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setTooltipPos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
        setHovered(country);
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
        setHovered(null);
    }, []);

    const selectedCountry = selected ? countryMap[selected.iso] || selected : null;
    const selectedHolding = selected ? activeHoldings[selected.iso] : null;
    const hoveredCountry = hovered ? countryMap[hovered.iso] || hovered : null;
    const hoveredHolding = hovered ? activeHoldings[hovered.iso] : null;

    const padding = densityMode === "compact" ? "p-2" : "p-3";

    return (
        <div
            ref={containerRef}
            className={`relative w-full ${padding}`}
            data-testid="african-portfolio-map"
        >
            {concentrated.length > 0 && (
                <div
                    className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400"
                    data-testid="concentration-alert-banner"
                >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="space-y-0.5">
                        {concentrated.map((c) => (
                            <div key={c.iso} data-testid={`concentration-alert-${c.iso}`}>
                                Concentration risk: <b>{c.pct.toFixed(1)}%</b> of your exposure is in <b>{c.name}</b>
                                {c.ticker ? ` (${c.ticker})` : ""} — consider diversifying.
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {/* Desktop: dual-column grid */}
            <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-12 gap-4"}`}>
                {/* Map Column */}
                <div className={isMobile ? "w-full" : "col-span-7"}>
                    <div className="relative w-full" style={{ aspectRatio: isMobile ? "1/1.1" : "4/3" }}>
                        <svg
                            ref={svgRef}
                            viewBox={VBOX}
                            className="w-full h-full"
                            style={{ touchAction: "pan-y pinch-zoom" }}
                            data-testid="africa-svg-map"
                        >
                            {/* Render country paths */}
                            {COUNTRY_PATHS.map((country) => {
                                const h = activeHoldings[country.iso];
                                const isExec = executingSet.has(country.iso);
                                const isSelected = selected?.iso === country.iso;
                                const isHovered = hovered?.iso === country.iso;

                                let fill = T.DEFAULT_FILL;
                                if (isExec) fill = T.EXEC_PULSE;
                                else if (h?.status === "holding") fill = exposureHeatColor(h.allocationPct);
                                else if (h?.status === "executing") fill = T.EXEC_PULSE;
                                else if (isHovered) fill = T.HOVER_FILL;

                                const stroke = (h && h.status !== "idle") || isSelected
                                    ? T.ACTIVE_STROKE
                                    : T.DARK_STROKE;
                                const sw = (h && h.status !== "idle") || isSelected ? 1.5 : 0.8;

                                return (
                                    <path
                                        key={country.iso}
                                        id={country.iso}
                                        d={country.d}
                                        fill={fill}
                                        stroke={stroke}
                                        strokeWidth={sw}
                                        strokeLinejoin="round"
                                        className={`cursor-pointer transition-colors duration-150 ${isExec ? "exec-pulse-anim" : ""}`}
                                        onClick={() => handleCountryClick(country)}
                                        onMouseMove={(e) => handleMouseMove(e, country)}
                                        onMouseLeave={handleMouseLeave}
                                        data-testid={`country-path-${country.iso}`}
                                    >
                                        <title>{country.name}{country.bourse ? ` (${country.bourse})` : ""}</title>
                                    </path>
                                );
                            })}

                            {/* Country labels for exchange countries */}
                            {COUNTRY_PATHS.filter((c) => c.bourse || activeHoldings[c.iso]).map((c) => (
                                <text
                                    key={`lbl-${c.iso}`}
                                    x={c.centroid[0]}
                                    y={c.centroid[1] + 2}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill="#94A3B8"
                                    fontSize="9"
                                    fontFamily="'Roboto', sans-serif"
                                    fontWeight="600"
                                    style={{ pointerEvents: "none", userSelect: "none" }}
                                >
                                    {c.iso}
                                </text>
                            ))}
                        </svg>

                        {/* Desktop tooltip */}
                        {!isMobile && hovered && (
                            <MapTooltip
                                country={hoveredCountry}
                                holding={hoveredHolding}
                                position={tooltipPos}
                            />
                        )}
                    </div>

                    {/* Legend chips */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        <LegendChip color={T.DEFAULT_FILL} label="Idle" />
                        <LegendChip color="#1D4ED8" label="Low exposure (<5%)" />
                        <LegendChip color="#2563EB" label="Moderate (5–15%)" />
                        <LegendChip color="#D97706" label="Elevated (15–30%)" />
                        <LegendChip color="#EA580C" label="Concentrated (>30%)" />
                        <LegendChip color={T.EXEC_PULSE} label="Executing" />
                    </div>
                </div>

                {/* Desktop Telemetry Drawer */}
                {!isMobile && (
                    <div className="col-span-5 rounded-lg border border-border/60 bg-card min-h-[300px]" data-testid="map-desktop-drawer">
                        <TelemetryDrawer
                            country={selectedCountry}
                            holding={selectedHolding}
                            onClose={() => setSelected(null)}
                            onNavigate={handleNavigateToExchange}
                        />
                    </div>
                )}
            </div>

            {/* Mobile Bottom Sheet */}
            {isMobile && selected && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 z-40"
                        onClick={() => setSelected(null)}
                        data-testid="bottom-sheet-overlay"
                    />
                    <BottomSheet
                        country={selectedCountry}
                        holding={selectedHolding}
                        onClose={() => setSelected(null)}
                        onNavigate={handleNavigateToExchange}
                    />
                </>
            )}
        </div>
    );
}

/* ── Legend chip ──────────────────────────────────────────────────── */
function LegendChip({ color, label, border }) {
    return (
        <div className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ background: color, border: border ? `1.5px solid ${border}` : undefined }}
            />
            {label}
        </div>
    );
}
